/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpHelperService } from '../../shared/services/http-helper.service';
import { filter, map, Observable } from 'rxjs';
import {
    CreditReviewForm,
    FinanceReviewForm,
    InvestigationReviewDomesticForm,
    InvestigationReviewInternationalForm,
    ManagerReviewForm
} from '../../cod/models/cod-review.model';
import { Page } from '../models/page-metadata.model';
import { GeneralCod } from '../../cod/models/cod.model';
import {
    ICodWithSubtype,
    ICreditReview,
    IInvestigatorReviewDom,
    IInvestigatorReviewInt,
    IManagerReview,
    IReassignment,
    ReviewResult,
    ReviewType
} from '@cod-monorepo/interfaces';
import { CodReviewMappingOperations } from '../../cod/operations/cod-review-mapping.operations';

@Injectable({
    providedIn: 'root'
})
export class CodService {
    protected baseUrl = environment.apiUrl;

    constructor(private httpClient: HttpClient, private httpHelperService: HttpHelperService) {}

    public submitCod<T extends { issueType: string }>(
        data: ICodWithSubtype<T>,
        endpoint: string
    ): Observable<ICodWithSubtype<T>> {
        return this.httpClient
            .post<ICodWithSubtype<T>>(`${environment.apiUrl}/cods/${endpoint}`, data)
            .pipe(this.httpHelperService.handleError('Cannot submit COD'));
    }

    public getAllCods(
        page: number,
        size: number,
        filters: { [key: string]: string[] | string }
    ): Observable<Page<GeneralCod>> {
        Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

        return this.httpClient
            .get<Page<GeneralCod>>(`${environment.apiUrl}/cods`, {
                params: {
                    page: page.toString(),
                    pageSize: size.toString(),
                    orderBy: ['createdAt'],
                    sortOrder: 'DESC',
                    ...filters
                }
            })
            .pipe(this.httpHelperService.handleError('Cannot fetch all CODs'));
    }

    public getMyCods(
        page: number,
        size: number,
        filters: { [key: string]: string[] | string }
    ): Observable<Page<GeneralCod>> {
        Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

        return this.httpClient
            .get<Page<GeneralCod>>(`${environment.apiUrl}/me/cods`, {
                params: {
                    page: page.toString(),
                    pageSize: size.toString(),
                    orderBy: ['createdAt'],
                    sortOrder: 'DESC',
                    ...filters
                }
            })
            .pipe(this.httpHelperService.handleError('Cannot fetch my CODs'));
    }

    public getMyTasks(
        page: number,
        size: number,
        filters: { [key: string]: string[] | string }
    ): Observable<Page<GeneralCod>> {
        Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

        return this.httpClient
            .get<Page<GeneralCod>>(`${environment.apiUrl}/me/tasks`, {
                params: {
                    page: page.toString(),
                    pageSize: size.toString(),
                    orderBy: ['createdAt'],
                    sortOrder: 'DESC',
                    ...filters
                }
            })
            .pipe(this.httpHelperService.handleError('Cannot fetch my tasks'));
    }

    public loadCodById(id: string): Observable<GeneralCod> {
        return this.httpClient
            .get<GeneralCod>(`${environment.apiUrl}/cods/${id}`)
            .pipe(this.httpHelperService.handleError('Cannot fetch COD data'));
    }

    public reassignCod(id: string, gid: string): Observable<void> {
        return this.httpClient.post<void>(`${environment.apiUrl}/cods/${id}/reviews/reassignment`, {
            assignee: gid,
            reviewResult: ReviewResult.REASSIGNED
        } as Omit<IReassignment, 'id' | 'createdBy' | 'createdAt' | 'reason'>);
    }

    public submitCreditReview(id: string, data: CreditReviewForm): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.apiUrl}/cods/${id}/reviews/credit-review`,
            CodReviewMappingOperations.mapICreditReviewFromCreditReviewForm(data)
        );
    }

    public loadCreditReview(id: string, attachments: string[]): Observable<CreditReviewForm> {
        return this.httpClient.get<ICreditReview>(`${environment.apiUrl}/cods/${id}/reviews/credit-review`).pipe(
            filter(value => !!value),
            map(review => CodReviewMappingOperations.mapCreditReviewFormFromIcreditReview(review, attachments))
        );
    }

    public submitMangerReview(id: string, data: ManagerReviewForm): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.apiUrl}/cods/${id}/reviews/manager-review`,
            CodReviewMappingOperations.mapIMangerReviewFromManagerReviewForm(data)
        );
    }

    public loadManagerReview(id: string): Observable<ManagerReviewForm> {
        return this.httpClient
            .get<IManagerReview[]>(`${environment.apiUrl}/cods/${id}/reviews/manager-reviews`, {
                params: {
                    type: ReviewType.MANAGER
                }
            })
            .pipe(
                filter(value => !!value),
                map(CodReviewMappingOperations.mapManagerReviewFormFromIManagerReview)
            );
    }

    public submitFinanceReview(id: string, data: FinanceReviewForm): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.apiUrl}/cods/${id}/reviews/finance-manager-review`,
            CodReviewMappingOperations.mapIManagerReviewFromFinanceReviewForm(data)
        );
    }

    public loadFinanceReview(id: string): Observable<FinanceReviewForm> {
        return this.httpClient
            .get<IManagerReview[]>(`${environment.apiUrl}/cods/${id}/reviews/manager-reviews`, {
                params: {
                    type: ReviewType.FINANCE_MANAGER
                }
            })
            .pipe(
                filter(value => !!value),
                map(CodReviewMappingOperations.mapFinanceReviewFormFromIManagerReview)
            );
    }

    public submitInvestigationReviewInternational(
        id: string,
        data: InvestigationReviewInternationalForm
    ): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.apiUrl}/cods/${id}/reviews/international/investigator-review`,
            CodReviewMappingOperations.mapIInvestigatorReviewIntFromInvestigationReviewInternationalForm(data)
        );
    }

    public loadInvestigationReviewInternational(
        id: string,
        attachments: string[]
    ): Observable<InvestigationReviewInternationalForm> {
        return this.httpClient
            .get<IInvestigatorReviewInt>(`${environment.apiUrl}/cods/${id}/reviews/investigator-review`)
            .pipe(
                filter(value => !!value),
                map(review =>
                    CodReviewMappingOperations.mapInvestigationReviewInternationalFormFromIInvestigatorReviewInt(
                        review,
                        attachments
                    )
                )
            );
    }

    public submitInvestigationReviewDomestic(id: string, data: InvestigationReviewDomesticForm): Observable<void> {
        return this.httpClient.post<void>(
            `${environment.apiUrl}/cods/${id}/reviews/domestic/investigator-review`,
            CodReviewMappingOperations.mapIInvestigatorReviewDomFromInvestigationReviewDomesticForm(data)
        );
    }

    public loadInvestigationReviewDomestic(
        id: string,
        attachments: string[]
    ): Observable<InvestigationReviewDomesticForm> {
        return this.httpClient
            .get<IInvestigatorReviewDom>(`${environment.apiUrl}/cods/${id}/reviews/investigator-review`)
            .pipe(
                filter(value => !!value),
                map(review =>
                    CodReviewMappingOperations.mapInvestigationReviewDomesticFormFromIInvestigatorReviewDom(
                        review,
                        attachments
                    )
                )
            );
    }

    public deleteCod(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${environment.apiUrl}/cods/${id}`);
    }
}
